import { useEffect } from "react";
import { getLocalAudioTrack } from "../Room/roomService";
import { useSelector } from "react-redux";

export default function DominantSpeakerAnalyzer({ participant, isUserAudioEnabled, onSpeakingChange, isLocalUser }) {
    const { startLocalAudio, allUserAudioStatus } = useSelector((state) => state.twilioEvents);
    const { remoteParticipantesDeviceInfo } = useSelector((state) => state.commonReducer);

    const localAudioTrack = isLocalUser ? getLocalAudioTrack() : null;

    const getAudioTrack = (participant) => {
        if (participant?.audioTracks?.size) {
            const track = Array.from(participant.audioTracks.values())[0].track;
            return track;
        }
        return null;
    };

    useEffect(() => {
        const audioTrack = isLocalUser ? localAudioTrack : getAudioTrack(participant);

        if (!audioTrack || !audioTrack.mediaStreamTrack) return;

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const stream = new MediaStream([audioTrack.mediaStreamTrack]);
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.85;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        source.connect(analyser);

        let speaking = false;
        let silenceStart = null;
        let animationFrameId;

        const volumeThreshold = 25;
        const silenceTimeout = 100;

        function checkVolume() {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            const isAboveThreshold = average > volumeThreshold;

            if (isAboveThreshold) {
                silenceStart = null;
                if (!speaking) {
                    speaking = true;
                }
            } else {
                const now = Date.now();
                if (!silenceStart) {
                    silenceStart = now;
                } else if (now - silenceStart > silenceTimeout && speaking) {
                    speaking = false;
                }
            }

            onSpeakingChange?.(speaking);

            animationFrameId = requestAnimationFrame(checkVolume);
        }

        checkVolume();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            try {
                source.disconnect();
                analyser.disconnect();
                audioContext.close();
            } catch (e) {
                console.warn("Cleanup error:", e);
            }
        };
    }, [participant, startLocalAudio, isUserAudioEnabled, remoteParticipantesDeviceInfo, allUserAudioStatus]);

    // Render based on context

    return null;
}
